import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Calendar } from 'lucide-react';
import { openCalendlyPopup } from '@/utils/calendly';
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstMenuItemRef = useRef<HTMLAnchorElement>(null);
  const location = useLocation();
  const navItems = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Particulieren',
    href: '/particulieren'
  }, {
    label: 'Studenten',
    href: '/studenten'
  }, {
    label: 'Zakelijk',
    href: '/zakelijk'
  }, {
    label: 'Hypotheken',
    href: '/hypotheken'
  }, {
    label: 'Serviceabonnement',
    href: '/serviceabonnement'
  }, {
    label: 'Schade',
    href: '/schade'
  }, {
    label: 'Contact',
    href: '/contact'
  }];

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  // Focus management for mobile menu
  useEffect(() => {
    if (isMenuOpen && firstMenuItemRef.current) {
      firstMenuItemRef.current.focus();
    }
  }, [isMenuOpen]);
  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const handleAppointment = () => {
    openCalendlyPopup();
  };

  // Scroll listener for logo shrink effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <header className="bg-background/95 backdrop-blur-sm border-b border-border sticky top-0 z-50 shadow-[0_3px_6px_-1px_rgba(0,0,0,0.1)]" role="banner">
      {/* Top bar with USPs */}
      <div className="bg-secondary text-secondary-foreground py-2">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between text-sm">
            <div className="hidden md:flex items-center space-x-8" role="list" aria-label="Onze voordelen">
              <div className="flex items-center space-x-2" role="listitem">
                <span className="w-2 h-2 bg-primary rounded-full" aria-hidden="true"></span>
                <span>Advies op maat</span>
              </div>
              <div className="flex items-center space-x-2" role="listitem">
                <span className="w-2 h-2 bg-primary rounded-full" aria-hidden="true"></span>
                <span>Transparant</span>
              </div>
              <div className="flex items-center space-x-2" role="listitem">
                <span className="w-2 h-2 bg-primary rounded-full" aria-hidden="true"></span>
                <span>Serviceabonnement</span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button size="sm" variant="outline" className="bg-transparent border-primary text-primary hover:bg-primary hover:text-primary-foreground focus:ring-2 focus:ring-primary focus:ring-offset-2" asChild aria-label="INLOGGEN DIGIMAP - Inloggen in uw persoonlijke omgeving">
                <a href="https://mijn.compleetverzekerd.nl/login" target="_blank" rel="noopener noreferrer">INLOGGEN DIGIMAP</a>
              </Button>
              <Button size="sm" className="bg-primary hover:bg-primary-dark text-primary-foreground focus:ring-2 focus:ring-primary-dark focus:ring-offset-2" onClick={handleAppointment} aria-label="Plan een afspraak">
                <Calendar className="w-4 h-4" />
                Plan een afspraak
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container mx-auto px-4">
        <div className={`flex items-center justify-between transition-all duration-300 ease-in-out ${
          isScrolled ? 'h-12' : 'h-16'
        }`}>
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 z-20 relative" aria-label="Compleet Verzekerd - Homepage">
            <img 
              src="/assets/7e3512ca-fe6e-49ba-8835-eea810c01d25.png" 
              alt="Compleet Verzekerd Logo" 
              width="216"
              height="64"
              sizes="216px"
              loading="lazy"
              decoding="async"
              className={`w-auto object-contain transition-all duration-300 ease-in-out ${
                isScrolled ? 'h-12' : 'h-16 translate-y-0'
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6" role="navigation" aria-label="Hoofdnavigatie">
            {navItems.map(item => {
              const isActive = location.pathname === item.href;
              return (
                <Link 
                  key={item.href} 
                  to={item.href} 
                  className={`nav-link text-foreground hover:text-primary transition-colors font-medium focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded px-2 py-1 relative ${
                    isActive ? 'text-primary active' : ''
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile menu button */}
          <button ref={menuButtonRef} className="lg:hidden p-2 focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded" onClick={handleMenuToggle} aria-expanded={isMenuOpen} aria-controls="mobile-menu" aria-label={isMenuOpen ? 'Sluit menu' : 'Open menu'}>
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && <div id="mobile-menu" className="lg:hidden py-4 border-t border-border" role="navigation" aria-label="Mobiele navigatie">
            <nav className="flex flex-col space-y-2">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link 
                    key={item.href} 
                    ref={index === 0 ? firstMenuItemRef : undefined} 
                    to={item.href} 
                    className={`nav-link px-4 py-2 text-foreground hover:text-primary hover:bg-muted rounded-md transition-colors focus:ring-2 focus:ring-primary focus:ring-offset-2 relative ${
                      isActive ? 'text-primary bg-muted' : ''
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                    {isActive && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full"></div>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>}
      </div>
    </header>;
};
export default Header;